/**
 * IELTS back office: lists read in full (every page), for the course tree (the lessons of one
 * level) and the questions of one passage. The keys start with the API path, so the usual
 * refresh of the Academy (`useRefreshAcademy`) reloads them too.
 */

import { academyLessonsList, academyQuestionsList, type ExamEnum, type Lesson, type Question, type QuestionSkillEnum } from "@agencyos/api-client";
import { useQuery } from "@tanstack/react-query";

/** Safety net: at most this many pages of 25 rows are read for one tree or one passage. */
const MAX_PAGES = 20;

async function allPages<T>(fetchPage: (page: number) => Promise<{ results: T[]; next?: string | null }>): Promise<T[]> {
  const rows: T[] = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const data = await fetchPage(page);
    rows.push(...data.results);
    if (!data.next) break;
  }
  return rows;
}

/** Every lesson of one level of the course (all statuses), in the course order. */
export function useLevelLessons(exam: ExamEnum, level: string) {
  return useQuery({
    queryKey: ["/api/v1/academy/lessons/", "all", exam, level],
    enabled: Boolean(level),
    queryFn: () =>
      allPages<Lesson>(async (page) => {
        const response = await academyLessonsList({ exam, level, page });
        return response.data;
      }),
  });
}

/** The questions attached to one passage (the API filters by exam and skill; the passage here). */
export function usePassageQuestions(exam: ExamEnum, skill: QuestionSkillEnum, passage: string) {
  return useQuery({
    queryKey: ["/api/v1/academy/questions/", "passage", exam, skill, passage],
    queryFn: async () => {
      const rows = await allPages<Question>(async (page) => {
        const response = await academyQuestionsList({ exam, skill, page });
        return response.data;
      });
      return rows.filter((q) => q.passage === passage).sort((a, b) => (a.number ?? 0) - (b.number ?? 0));
    },
  });
}
